import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '@parrot-software/observability';
import type { InvoiceDataViewDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import type { InvoiceDataFormMode } from '@subscriptions/presentation/components/specific/InvoiceDataForm';
import type { InvoiceDataDeepLinkMode } from '@subscriptions/presentation/constants';
import type { AnalyticsStatus, InvoicingChoice, InvoicingMethod } from '@subscriptions/presentation/constants';
import {
  AnalyticsStatuses,
  InvoiceDataDeepLinkModes,
  InvoicingChoices,
  InvoicingMethods,
  InvoicingSources,
  NO_INVOICING_VALUE,
  RetryScopes,
} from '@subscriptions/presentation/constants';
import {
  useDeleteInvoiceData,
  useSubscriptionBlocks,
  useUpdateBlockInvoiceData,
} from '@subscriptions/presentation/hooks';
import { useInvoiceDataSheetParams } from '@subscriptions/presentation/hooks/useInvoiceDataSheetParams';
import { useSubscriptionAnalytics } from '@subscriptions/presentation/hooks/useSubscriptionAnalytics';

const invoiceLog = logger.forDomain('SUB', { subdomain: 'billing' });

/** Tab the retry flow hands the user back to — must match the key BlockDetailTabs reads */
const PAYMENT_HISTORY_TAB = 'paymentHistory';

/**
 * Beat between the retry success toast and the tab switch, so the confirmation is read on the
 * tab the user acted on instead of being carried into a view that changes under it.
 */
const RETRY_REDIRECT_DELAY_MS = 1000;

/**
 * Encapsulates the create/edit/delete state machine for invoice data actions
 * inside the block invoicing tab.
 *
 * @param block - Current subscription block
 * @returns State and handlers for sheet, delete dialog, blocked dialog and radio selection
 */
export const useInvoiceDataActions = (block: SubscriptionBlockViewDto) => {
  const { t } = useTranslation('invoiceData');
  const history = useHistory();
  const { data: allBlocks } = useSubscriptionBlocks();
  const { mutateAsync: updateBlockInvoiceData, isLoading: isUpdating } = useUpdateBlockInvoiceData();
  const { mutateAsync: deleteInvoiceData, isLoading: isDeleting } = useDeleteInvoiceData();
  const { clearTarget, onlyFailed, paymentUuid, setTarget } = useInvoiceDataSheetParams();
  const { trackInvoiceRetryStarted, trackInvoicingSelected } = useSubscriptionAnalytics();

  const trackInvoicing = useCallback(
    (choice: InvoicingChoice, status: AnalyticsStatus, method?: InvoicingMethod) =>
      trackInvoicingSelected({ blockUuid: block.uuid, choice, method, source: InvoicingSources.BLOCK_DETAIL, status }),
    [block.uuid, trackInvoicingSelected],
  );

  const currentValue = block.invoiceData?.uuid ?? NO_INVOICING_VALUE;
  const [selectedValue, setSelectedValue] = useState(currentValue);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<InvoiceDataFormMode>('create');
  const [editingInvoiceData, setEditingInvoiceData] = useState<InvoiceDataViewDto | null>(null);
  const [retryErrorMessage, setRetryErrorMessage] = useState<string | null>(null);
  const [deletingInvoiceData, setDeletingInvoiceData] = useState<InvoiceDataViewDto | null>(null);
  const [blockedInvoiceData, setBlockedInvoiceData] = useState<InvoiceDataViewDto | null>(null);
  const [isNoInvoicingDialogOpen, setIsNoInvoicingDialogOpen] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // The redirect is deferred, so it must not fire against a page the user already left.
  useEffect(
    () => () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    },
    [],
  );

  const handleValueChange = useCallback(
    async (value: string) => {
      if (value === selectedValue || isUpdating) return;

      if (value === NO_INVOICING_VALUE) {
        setIsNoInvoicingDialogOpen(true);
        return;
      }

      setSelectedValue(value);

      try {
        await updateBlockInvoiceData({ blockUuid: block.uuid, dto: { invoiceDataUuid: value } });
        trackInvoicing(InvoicingChoices.INVOICE, AnalyticsStatuses.SUCCESS, InvoicingMethods.EXISTING);
        toast.success(t('blockInvoicing.updateSuccess'));
      } catch {
        trackInvoicing(InvoicingChoices.INVOICE, AnalyticsStatuses.FAILED, InvoicingMethods.EXISTING);
        setSelectedValue(currentValue);
        toast.error(t('blockInvoicing.updateError'));
      }
    },
    [block.uuid, currentValue, isUpdating, selectedValue, t, trackInvoicing, updateBlockInvoiceData],
  );

  const handleConfirmNoInvoicing = useCallback(async () => {
    setIsNoInvoicingDialogOpen(false);
    setSelectedValue(NO_INVOICING_VALUE);

    try {
      await updateBlockInvoiceData({ blockUuid: block.uuid, dto: { invoiceDataUuid: null } });
      trackInvoicing(InvoicingChoices.NO_INVOICE, AnalyticsStatuses.SUCCESS);
      toast.success(t('blockInvoicing.updateSuccess'));
    } catch {
      trackInvoicing(InvoicingChoices.NO_INVOICE, AnalyticsStatuses.FAILED);
      setSelectedValue(currentValue);
      toast.error(t('blockInvoicing.updateError'));
    }
  }, [block.uuid, currentValue, t, trackInvoicing, updateBlockInvoiceData]);

  const handleCancelNoInvoicing = useCallback(() => {
    setIsNoInvoicingDialogOpen(false);
  }, []);

  const handleNoInvoicingDialogChange = useCallback((open: boolean) => {
    if (!open) setIsNoInvoicingDialogOpen(false);
  }, []);

  const handleOpenSheetChange = useCallback(
    (open: boolean) => {
      setIsSheetOpen(open);
      if (!open) clearTarget();
    },
    [clearTarget],
  );

  const handleSheetCloseComplete = useCallback(() => {
    setEditingInvoiceData(null);
    setSheetMode('create');
    setRetryErrorMessage(null);
  }, []);

  const handleCancelSheet = useCallback(() => {
    handleOpenSheetChange(false);
  }, [handleOpenSheetChange]);

  const handleOpenCreate = useCallback(() => {
    setEditingInvoiceData(null);
    setSheetMode('create');
    setIsSheetOpen(true);
    // Create mode has no record to point at, so the url must not keep a stale target.
    clearTarget();
  }, [clearTarget]);

  const openSheetForRecord = useCallback(
    (item: InvoiceDataViewDto, nextMode: InvoiceDataDeepLinkMode, nextError: string | null = null) => {
      setEditingInvoiceData(item);
      setSheetMode(nextMode);
      setRetryErrorMessage(nextError);
      setIsSheetOpen(true);
      setTarget(item.uuid, nextMode, nextError);
    },
    [setTarget],
  );

  const handleEdit = useCallback(
    (item: InvoiceDataViewDto) => openSheetForRecord(item, InvoiceDataDeepLinkModes.EDIT),
    [openSheetForRecord],
  );

  const handleRetry = useCallback(
    (item: InvoiceDataViewDto, errorMessage?: string | null) => {
      invoiceLog.info('openRetryInvoiceSheet', {
        'invoice.paymentUuid': paymentUuid ?? undefined,
        'invoice.onlyFailed': onlyFailed,
        'invoice.dataUuid': item.uuid,
        'invoice.hasFailureReason': Boolean(errorMessage),
        'invoice.subscriptionUuid': block.uuid,
      });
      trackInvoiceRetryStarted({
        blockUuid: block.uuid,
        hasFailureReason: Boolean(errorMessage),
        scope: onlyFailed ? RetryScopes.FAILED_ONLY : RetryScopes.FULL_PAYMENT,
      });
      openSheetForRecord(item, InvoiceDataDeepLinkModes.RETRY, errorMessage ?? null);
    },
    [block.uuid, onlyFailed, openSheetForRecord, paymentUuid, trackInvoiceRetryStarted],
  );

  const handleDelete = useCallback(
    (item: InvoiceDataViewDto) => {
      const assignedBlocks = allBlocks?.filter((b) => b.invoiceData?.uuid === item.uuid) ?? [];
      if (assignedBlocks.length > 0) {
        setBlockedInvoiceData(item);
        return;
      }
      setDeletingInvoiceData(item);
    },
    [allBlocks],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingInvoiceData) return;

    try {
      await deleteInvoiceData(deletingInvoiceData.uuid);
      toast.success(t('deleteSuccess'));
      setDeletingInvoiceData(null);
    } catch {
      toast.error(t('deleteError'));
    }
  }, [deleteInvoiceData, deletingInvoiceData, t]);

  const handleCreateSuccess = useCallback(
    async (createdInvoiceData: InvoiceDataViewDto) => {
      try {
        setSelectedValue(createdInvoiceData.uuid);
        await updateBlockInvoiceData({ blockUuid: block.uuid, dto: { invoiceDataUuid: createdInvoiceData.uuid } });
        trackInvoicing(InvoicingChoices.INVOICE, AnalyticsStatuses.SUCCESS, InvoicingMethods.NEW);
        setIsSheetOpen(false);
        clearTarget();
        toast.success(t('invoiceDataForm.blockSuccess'));
      } catch {
        trackInvoicing(InvoicingChoices.INVOICE, AnalyticsStatuses.FAILED, InvoicingMethods.NEW);
        setSelectedValue(currentValue);
        toast.error(t('blockInvoicing.updateError'));
      }
    },
    [block.uuid, clearTarget, currentValue, t, trackInvoicing, updateBlockInvoiceData],
  );

  const closeSheetWith = useCallback(
    (message: string) => {
      setIsSheetOpen(false);
      setEditingInvoiceData(null);
      setSheetMode('create');
      clearTarget();
      toast.success(message);
    },
    [clearTarget],
  );

  const handleEditSuccess = useCallback(() => closeSheetWith(t('updateSuccess')), [closeSheetWith, t]);

  // Retry reports on the payment it re-ran, not on the fiscal data it saved on the way there —
  // the form only calls this once the retry itself resolved.
  const handleRetrySuccess = useCallback(() => {
    closeSheetWith(t('retrySuccess'));

    // The re-run charge shows up in the payment history, so that is where the user is left.
    // Read from `history.location` at fire time rather than closing over the search string,
    // since `closeSheetWith` has just rewritten it.
    redirectTimerRef.current = setTimeout(() => {
      const { pathname, search } = history.location;
      const params = new URLSearchParams(search);
      params.set('tab', PAYMENT_HISTORY_TAB);
      history.replace({ pathname, search: `?${params.toString()}` });
    }, RETRY_REDIRECT_DELAY_MS);
  }, [closeSheetWith, history, t]);

  const handleDeleteDialogChange = useCallback((open: boolean) => {
    if (!open) setDeletingInvoiceData(null);
  }, []);

  const handleBlockedDialogChange = useCallback((open: boolean) => {
    if (!open) setBlockedInvoiceData(null);
  }, []);

  const handleBlockedDialogClose = useCallback(() => {
    setBlockedInvoiceData(null);
  }, []);

  // Read straight off the url rather than mirrored into state: the retry link is the only
  // writer, and `setTarget` preserves the param for as long as the sheet stays in retry mode.
  const retryPaymentUuid = sheetMode === 'retry' ? paymentUuid : null;
  const retryOnlyFailed = sheetMode === 'retry' && onlyFailed;

  return {
    selectedValue,
    isSheetOpen,
    sheetMode,
    editingInvoiceData,
    retryErrorMessage,
    retryPaymentUuid,
    retryOnlyFailed,
    deletingInvoiceData,
    blockedInvoiceData,
    isNoInvoicingDialogOpen,
    isDeleting,
    isUpdating,
    handleValueChange,
    handleOpenSheetChange,
    handleSheetCloseComplete,
    handleCancelSheet,
    handleOpenCreate,
    handleEdit,
    handleRetry,
    handleDelete,
    handleConfirmDelete,
    handleCreateSuccess,
    handleEditSuccess,
    handleRetrySuccess,
    handleDeleteDialogChange,
    handleConfirmNoInvoicing,
    handleCancelNoInvoicing,
    handleNoInvoicingDialogChange,
    handleBlockedDialogChange,
    handleBlockedDialogClose,
  };
};
